import { Component } from 'react';
import * as React from 'react';
import { FormattedDate, FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import moment, { Moment } from 'moment';

import { getApiStats } from 'lib/api';
import Dropdown from 'components/Base/Dropdown';
import thematize from '../../lib/thematize';
import DataTable from '../DataTable';
import messages from './messages';
import styles from './Stats.scss';

const Month = ({ date }: { date: Moment }): React.ReactElement => (
  <FormattedDate value={date.toDate()} year="numeric" month="long" />
);

Month.defaultProps = {
  date: moment(),
};

interface Props {
  intl: IntlShape;
}

interface State {
  from: Moment;
  loading: boolean;
  payload: API$ApiStats;
  to: Moment;
}

class Stats extends Component<Props, State> {
  state = {
    loading: true,
    payload: [],
    from: moment().startOf('month'),
    to: moment(),
  };

  theme = thematize(styles);

  componentDidMount(): void {
    this.receiveStats(this.state.from, this.state.to);
  }

  handleDateChange = (to: Moment): void => {
    const from = moment(to).startOf('month');

    this.setState({ from, to });
    this.receiveStats(from, to);
  };

  receiveStats(from: Moment, to: Moment): void {
    this.setState({ loading: true });
    // backend expects `YYYY-MM-DD_YYYY-MM-DD`, upper bound is exclusive
    const dateRange = `${from.format('YYYY-MM-DD')}_${moment(to)
      .endOf('month')
      .add(1, 'day')
      .format('YYYY-MM-DD')}`;

    getApiStats(dateRange).then(
      ({ payload }) => {
        this.setState({
          loading: false,
          payload,
        });
      },
      (err) => {
        this.setState({
          loading: false,
          payload: err,
        });
      }
    );
  }

  *monthsGenerator(start: Moment, today: Moment): Generator<Moment, void> {
    const date = moment(start).add(30, 'days').endOf('month');

    if (date.isBefore(today, 'month')) {
      yield date;
      yield* this.monthsGenerator(date, today);
    }
  }

  renderMonths(): React.ReactElement {
    const start = moment('2016-08-31');
    const today = moment();
    const months = [start, ...this.monthsGenerator(start, today), today].reverse();

    return (
      <>
        {months.map((month, i) => (
          <Dropdown.Option
            // eslint-disable-next-line
            key={i}
            onOptionClick={() => this.handleDateChange(month)}
          >
            <Month date={month} />
          </Dropdown.Option>
        ))}
      </>
    );
  }

  renderNoData(): React.ReactElement {
    return (
      <div className={this.theme('no-data')}>
        {this.state.loading ? (
          <FormattedMessage {...messages['statsLoading']} />
        ) : (
          <FormattedMessage {...messages['statsNoData']} />
        )}
      </div>
    );
  }

  renderData(): React.ReactElement {
    const { formatMessage } = this.props.intl;

    return (
      <DataTable
        columns={[
          {
            name: 'path',
            title: formatMessage(messages['statsResource']),
          },
          {
            name: 'count',
            title: formatMessage(messages['statsCount']),
            align: 'right',
          },
        ]}
        data={this.state.payload}
      />
    );
  }

  render(): React.ReactElement {
    const { to, payload } = this.state;
    const currentMonth = (
      <div className={this.theme('heading__link')}>
        <Month date={to} />
      </div>
    );

    return (
      <div className={this.theme('container')}>
        <h2 className={this.theme('heading')}>
          <FormattedMessage {...messages['statsHeading']} />{' '}
          <Dropdown bodyClassName={this.theme('dropdownBody')} button={currentMonth}>
            {this.renderMonths()}
          </Dropdown>
        </h2>

        <div>{payload && payload.length ? this.renderData() : this.renderNoData()}</div>
      </div>
    );
  }
}

export default injectIntl(Stats);
